import { TablescapeEvent } from "@root/tablescape/utilities/tablescapeContext";
import { type Context } from "@tablescape/state";
import {
  findSelectedVariants,
  formatMoney,
  getSelectedVariantsCompareAtPrice,
  getSelectedVariantsPrice,
  intelligemsCompareAtPrice,
  intelligemsPrice,
} from "@tablescape/utilities";
import { component } from "picoapp";

export const price = component<Context>((node, context) => {
  const priceTextNode = node.querySelector("[data-tbsp-component='text']");
  const footnoteSymbolNode = node.querySelector(
    "[data-product-footnote-symbol]"
  );
  const parentBundlePriceString = node.dataset.parentBundlePrice;
  const productIdData = node.dataset.productId;
  const discountType = node.dataset.discountType;
  const isGiftCard = node.dataset.giftCard === "true";

  // Initialize display from server-rendered cents using locale-aware formatter
  const initialPriceCentsStr = node.dataset.priceCents;
  const initialPriceCents = initialPriceCentsStr
    ? Number(initialPriceCentsStr)
    : undefined;
  if (priceTextNode && initialPriceCents !== undefined) {
    priceTextNode.textContent = initialPriceCents
      ? `${formatMoney(
          initialPriceCents,
          "{{ amount_no_trailing_zeroes }}",
          false
        )}`
      : "";
  }

  const parentBundlePrice = parentBundlePriceString
    ? parseInt(parentBundlePriceString)
    : undefined;

  const getPriceFromProductFormInputs = (
    checkedInputs: NodeListOf<Element>,
    parentBundlePrice: number,
    isKitted: boolean
  ) => {
    const inputPriceSum =
      Array.from(checkedInputs).reduce((acc, input: Element) => {
        const selectedVariantPrice =
          input.parentElement?.dataset.mtrProductVariantPrice;
        if (selectedVariantPrice) {
          return acc + Number(selectedVariantPrice);
        }
        return acc;
      }, 0) * 100;

    if (isKitted) {
      return inputPriceSum;
    }

    return Math.min(inputPriceSum, parentBundlePrice);
  };

  context.on(
    TablescapeEvent.VARIANT_PRICE_UPDATED,
    ({ discounts, products, selectedVariant }) => {
      if (!products) {
        return;
      }

      if (
        discountType !== "bundle" &&
        productIdData !== selectedVariant?.productId
      ) {
        return;
      }

      let selectedVariants = [];

      if (discountType === "bundle") {
        selectedVariants = findSelectedVariants(products);
      } else if (selectedVariant) {
        selectedVariants = [selectedVariant];
      } else {
        return;
      }

      // For gift cards, don't pass parentBundlePrice (it's an optional param)
      let price = isGiftCard
        ? getSelectedVariantsPrice(selectedVariants)
        : getSelectedVariantsPrice(selectedVariants, parentBundlePrice);

      /* The conditional statement below handles the case where not all variants of a product
      fixed bundle have been selected via UI yet, and thus is not in TablescapeContext state.
      In this case, the price is computed from data attributes of the selected swatches. */

      if (
        discountType === "bundle" &&
        Object.values(products).filter((product) => !!product).length < 2 &&
        parentBundlePrice
      ) {
        const checkedInputs = document.querySelectorAll(
          "#product-form input[type='radio'][checked]"
        );
        const isKitted =
          checkedInputs.length === 1 &&
          !!checkedInputs[0].closest("[data-kitted-product=true");

        price = getPriceFromProductFormInputs(
          checkedInputs,
          parentBundlePrice,
          isKitted
        );
      }

      let compareAtPrice = getSelectedVariantsCompareAtPrice(selectedVariants);
      const discount = discounts?.find(
        (discount) => discount.productId === productIdData
      );

      const hasDiscount =
        discount && discount.type === node.dataset.discountType;

      if (window.igData) {
        price = intelligemsPrice(selectedVariants) || price;
        compareAtPrice =
          intelligemsCompareAtPrice(selectedVariants) || compareAtPrice;
      }

      if (hasDiscount && (!compareAtPrice || price >= compareAtPrice)) {
        price -= discount.value;
      }

      if (priceTextNode) {
        priceTextNode.textContent = price
          ? `${formatMoney(price, "{{ amount_no_trailing_zeroes }}", false)}`
          : "";
      }

      const showsCompareAtPrice =
        !!compareAtPrice || !!hasDiscount || discountType === "bundle";

      if (showsCompareAtPrice) {
        priceTextNode?.classList.add("text-special");
      } else {
        priceTextNode?.classList.remove("text-special");
      }

      // The product footnote symbol moves to the compare-at price when there is one
      footnoteSymbolNode?.classList.toggle("hidden", showsCompareAtPrice);
    }
  );
});

export const compareAtPrice = component<Context>((node, context) => {
  const priceTextNode = node.querySelector("[data-tbsp-component='text']");
  const footnoteSymbolNode = node.querySelector(
    "[data-product-footnote-symbol]"
  );
  const productIdData = node.dataset.productId;
  const discountType = node.dataset.discountType;
  const compareAtPriceDisplay = node.dataset.compareAtPriceDisplay;

  // Initialize compare-at price from server-rendered cents using locale-aware formatter
  const initialCompareAtCentsStr = node.dataset.compareAtPriceCents;
  const initialCompareAtCents = initialCompareAtCentsStr
    ? Number(initialCompareAtCentsStr)
    : undefined;
  if (priceTextNode && initialCompareAtCents !== undefined) {
    const formattedCompareAtPrice = initialCompareAtCents
      ? `${formatMoney(
          initialCompareAtCents,
          "{{ amount_no_trailing_zeroes }}",
          false
        )}`
      : "";
    priceTextNode.textContent =
      compareAtPriceDisplay && formattedCompareAtPrice
        ? compareAtPriceDisplay.replace("{{ price }}", formattedCompareAtPrice)
        : formattedCompareAtPrice;
  }

  context.on(
    TablescapeEvent.VARIANT_PRICE_UPDATED,
    ({ discounts, products, selectedVariant }) => {
      if (!products) return;

      if (
        discountType !== "bundle" &&
        productIdData !== selectedVariant?.productId
      ) {
        return;
      }

      let selectedVariants = [];

      if (discountType === "bundle") {
        if (Object.keys(products).length < 2) {
          return;
        }
        selectedVariants = findSelectedVariants(products);
      } else if (selectedVariant) {
        selectedVariants = [selectedVariant];
      } else {
        return;
      }

      const discount = discounts?.find(
        (discount) => discount.productId === productIdData
      );

      let price = getSelectedVariantsPrice(selectedVariants);
      let _compareAtPrice = getSelectedVariantsCompareAtPrice(
        selectedVariants,
        discountType
      );

      if (window.igData) {
        price = intelligemsPrice(selectedVariants) || price;
        _compareAtPrice =
          intelligemsCompareAtPrice(selectedVariants) || _compareAtPrice;
      }

      let compareAtPrice = _compareAtPrice;
      if (
        discount &&
        discount.type === node.dataset.discountType &&
        !_compareAtPrice
      ) {
        if (!_compareAtPrice) {
          compareAtPrice = price;
        }
      }
      if (priceTextNode) {
        const formattedCompareAtPrice = compareAtPrice
          ? `${formatMoney(
              compareAtPrice,
              "{{ amount_no_trailing_zeroes }}",
              false
            )}`
          : "";
        priceTextNode.textContent =
          compareAtPriceDisplay && formattedCompareAtPrice
            ? compareAtPriceDisplay.replace(
                "{{ price }}",
                formattedCompareAtPrice
              )
            : formattedCompareAtPrice;
      }

      footnoteSymbolNode?.classList.toggle("hidden", !compareAtPrice);
    }
  );
});
